import React, { useEffect, useRef, useState } from 'react';
import './Charts.css';

const HeatMap = ({ 
  data = [], 
  title = 'User Interaction Heat Map',
  width = 800,
  height = 400,
  screenshot = null,
  pageUrl = '/',
  colorScale = 'hot', // 'hot', 'cool', or 'rainbow'
  showLabels = true
}) => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width, height });
  const containerRef = useRef(null);

  // Resize handler for responsive canvas
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        // Maintain aspect ratio
        const height = width * (dimensions.height / dimensions.width);
        setDimensions({ width, height });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dimensions.height, dimensions.width]);

  useEffect(() => {
    if (canvasRef.current && data.length > 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions
      canvas.width = dimensions.width;
      canvas.height = dimensions.height;
      
      // Clear canvas
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);

      // Draw screenshot as background if provided
      if (screenshot) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);
          drawHeatmapPoints();
        };
        img.src = screenshot;
      } else {
        // Draw page outline
        ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
        ctx.fillRect(0, 0, dimensions.width, dimensions.height);
        
        // Draw page elements outline (simplified)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        
        // Header area
        ctx.strokeRect(0, 0, dimensions.width, dimensions.height * 0.1);
        
        // Content area
        ctx.strokeRect(dimensions.width * 0.1, dimensions.height * 0.15, 
                      dimensions.width * 0.8, dimensions.height * 0.7);
        
        // Draw URL
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.font = '12px sans-serif';
        ctx.fillText(pageUrl, 10, 20);
        
        drawHeatmapPoints();
      }
      
      function drawHeatmapPoints() {
        // Find max value for normalization
        const maxValue = Math.max(...data.map(point => point.value), 1);
        
        // Draw heatmap points
        data.forEach(point => {
          // Scale coordinates to canvas size
          const x = (point.x / 100) * dimensions.width;
          const y = (point.y / 100) * dimensions.height;
          
          const radius = Math.max(15, (point.value / maxValue) * 40);
          const alpha = Math.min(0.8, point.value / maxValue);
          
          let gradient;
          if (colorScale === 'hot') {
            gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
            gradient.addColorStop(0, `rgba(239, 68, 68, ${alpha})`);
            gradient.addColorStop(1, 'rgba(239, 68, 68, 0)');
          } else if (colorScale === 'cool') {
            gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
            gradient.addColorStop(0, `rgba(59, 130, 246, ${alpha})`);
            gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
          } else if (colorScale === 'rainbow') {
            gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
            const hue = (point.value / maxValue) * 240;
            gradient.addColorStop(0, `hsla(${hue}, 100%, 50%, ${alpha})`);
            gradient.addColorStop(1, `hsla(${hue}, 100%, 50%, 0)`);
          }
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, 2 * Math.PI);
          ctx.fill();
          
          // Add labels if enabled
          if (showLabels && point.value > maxValue * 0.5) {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.font = '10px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(point.label || point.value.toString(), x, y - radius - 5);
          }
        });
      }
    }
  }, [data, dimensions, screenshot, pageUrl, colorScale, showLabels]);

  return (
    <div className="chart heat-map" ref={containerRef}>
      <h3 className="chart-title">{title}</h3>
      <div className="heatmap-container">
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            display: 'block'
          }}
        />
        <div className="heatmap-legend">
          <div className="legend-item">
            <span className="legend-color" style={{background: 'rgba(239, 68, 68, 0.8)'}}></span>
            <span>High activity</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{background: 'rgba(239, 68, 68, 0.4)'}}></span>
            <span>Medium activity</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{background: 'rgba(239, 68, 68, 0.2)'}}></span>
            <span>Low activity</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeatMap; 